/**
 * React Query hooks for PlanTemplate and PlanPhase CRUD.
 * Replaces Zustand mock data for the Plan Builder.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPlanTemplate,
  deletePlanTemplate,
  duplicatePlanTemplate,
  fetchPlanTemplate,
  fetchPlanTemplates,
  updatePlanTemplate,
  addPlanPhase,
  updatePlanPhase,
  deletePlanPhase,
  type PlanTemplateCreate,
  type PlanPhaseCreate,
} from "@/lib/templateApi";
import { useAuthState } from "./useAuthState";

const QUERY_KEY = "planTemplates";

// ── Queries ───────────────────────────────────────────────────────────────────

export function usePlanTemplates(params?: {
  sport?: string;
  difficulty?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const { isLoaded, isAuthenticated } = useAuthState();

  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => fetchPlanTemplates(params),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlanTemplate(id: string | null) {
  const { isLoaded, isAuthenticated } = useAuthState();

  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => fetchPlanTemplate(id!),
    enabled: isLoaded && isAuthenticated && !!id,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Template mutations ────────────────────────────────────────────────────────

export function useCreatePlanTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlanTemplateCreate) => createPlanTemplate(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdatePlanTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<PlanTemplateCreate> }) =>
      updatePlanTemplate(id, payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.setQueryData([QUERY_KEY, data.id], data);
    },
  });
}

export function useDeletePlanTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePlanTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDuplicatePlanTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => duplicatePlanTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

// ── Phase mutations ───────────────────────────────────────────────────────────

export function useAddPlanPhase(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlanPhaseCreate) => addPlanPhase(templateId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, templateId] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdatePlanPhase(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, payload }: { phaseId: string; payload: Partial<PlanPhaseCreate> }) =>
      updatePlanPhase(templateId, phaseId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, templateId] });
    },
  });
}

export function useDeletePlanPhase(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phaseId: string) => deletePlanPhase(templateId, phaseId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, templateId] });
    },
  });
}
